import { FC, useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Loading } from "../../components/Loading";
import "./index.css";
import Swal from "sweetalert2";
import { useAppConfig } from "../../hooks/useAppConfig";
import { ConfigType } from "../../contexts/AppConfigContext";
import { Menu } from "../../components/Menu";
import { Header } from "../../components/Header";
import { LeftMenu } from "../../components/LeftMenu";
import { useSeller } from "../../hooks/useSeller";
import { SellerQueueCard } from "../../components/SellerQueueCard";
import { useSellerQueue } from "../../hooks/useSellerQueue";
import { useSellerSession, NoSaleFormData } from "../../hooks/useSellerSession";
import { useSellerQueueRealtime, SellerQueueRealtimeEvent } from "../../hooks/useSellerQueueRealtime";
import { AttendanceModal } from "../../components/AttendanceModal";
import { useSellerQueueContext } from "../../contexts/SellerQueueContext";
import {
  closeSellerAttendance,
  addSessionCustomer,
  completeSessionCustomer,
  getSessionCustomers,
  getSellerQueueConfig,
  getCurrentSession as getCurrentSellerSession,
  returnSellerToQueueSeller,
} from "../../services/sellerQueueService";
import { SellerServiceSessionCustomerItem } from "../../contexts/SellerQueueContext";

const SellerAttendance: FC = () => {
  const { getSellerAppConfig } = useAppConfig();
  const { getTokenSeller } = useSeller();
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [config, setConfig] = useState<ConfigType | void>();

  const queueHook = useSellerQueue();
  const { queueEntry, isLoading: isLoadingQueue, error: errorQueue, refetchQueue } = queueHook;
  const { enterQueue, leaveQueue } = queueHook;

  const sessionHook = useSellerSession();
  const { currentSession, startService, getElapsedSeconds, refetchSession } = sessionHook;
  const [sessionCustomers, setSessionCustomers] = useState<SellerServiceSessionCustomerItem[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [isCustomerResultMode, setIsCustomerResultMode] = useState(false);
  const [showEndServiceModal, setShowEndServiceModal] = useState(false);
  const [serviceEndIntent, setServiceEndIntent] = useState<"return_to_queue" | "end_and_leave_queue" | null>(null);
  const [sellerQueueConfig, setSellerQueueConfig] = useState<{
    multiCustomerEnabled: boolean;
    maxCustomersPerSession: number;
  } | null>(null);

  useEffect(() => {
    refetchQueue();
    refetchSession();
  }, [refetchQueue, refetchSession]);

  useEffect(() => {
    getSellerQueueConfig().then((queueConfig) => {
      if (!queueConfig) return;
      setSellerQueueConfig({
        multiCustomerEnabled: Boolean(queueConfig.multiCustomerEnabled),
        maxCustomersPerSession: Number(queueConfig.maxCustomersPerSession || 1),
      });
    });
  }, []);

  useEffect(() => {
    if (showEndServiceModal) return;

    if (!currentSession?.id || currentSession.status !== "in_progress") {
      setSessionCustomers([]);
      setSelectedCustomerId(null);
      return;
    }

    let active = true;
    const loadSessionCustomers = async () => {
      try {
        const customers = await getSessionCustomers(currentSession.id);
        if (!active) return;
        setSessionCustomers(customers);
        setSelectedCustomerId((previous) => previous || customers.find((item) => item.status === "in_progress")?.id || null);
      } catch {
        if (active) setSessionCustomers([]);
      }
    };

    void loadSessionCustomers();
    const interval = window.setInterval(() => {
      void loadSessionCustomers();
    }, 5000);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [currentSession?.id, currentSession?.status, showEndServiceModal]);

  const { setQueueEntry, setCurrentSession } = useSellerQueueContext();

  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [attendanceMenuOpen, setAttendanceMenuOpen] = useState(false);
  const queueEntryRef = useRef(queueEntry);

  useEffect(() => {
    queueEntryRef.current = queueEntry;
  }, [queueEntry]);

  const showNotification = (type: "success" | "error", message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    const maybeResponse = (error as any)?.response?.data;
    return (
      maybeResponse?.message ||
      maybeResponse?.error ||
      (error instanceof Error ? error.message : null) ||
      fallback
    );
  };

  const handleRealtimeEvent = useCallback((event: SellerQueueRealtimeEvent) => {
    console.log("[SellerAttendance] Real-time event:", event);

    if (event.type === "called") {
      showNotification("success", "📞 Você foi chamado! Clique em 'Iniciar Atendimento'");
    } else if (event.type === "position-updated") {
      const currentQueueEntry = queueEntryRef.current;
      if (event.data && currentQueueEntry) {
        setQueueEntry({
          ...currentQueueEntry,
          position: event.data.position,
        });
      }
    } else if (event.type === "status-changed") {
      const currentQueueEntry = queueEntryRef.current;
      if (event.data && currentQueueEntry) {
        setQueueEntry({
          ...currentQueueEntry,
          status: event.data.status,
        });
      }
    }
  }, [setQueueEntry]);
  useSellerQueueRealtime(handleRealtimeEvent);

  useEffect(() => {
    if (queueEntry?.status !== "in_service") return;
    if (currentSession?.status === "in_progress") return;

    refetchSession();
  }, [queueEntry?.status, currentSession?.status, refetchSession]);

  useEffect(() => {
    if (!currentSession || currentSession.status !== "in_progress") {
      setElapsedTime(0);
      return;
    }

    setElapsedTime(getElapsedSeconds());

    const interval = setInterval(() => {
      setElapsedTime(getElapsedSeconds());
    }, 1000);

    return () => clearInterval(interval);
  }, [currentSession, getElapsedSeconds]);

  const getConfig = useCallback(async () => {
    try {
      setLoadingHeader(true);
      const sellerConfig = await getSellerAppConfig();
      setConfig(sellerConfig);
      setLoadingHeader(false);
    } catch (error) {
      console.error("Error loading seller config:", error);
      setLoadingHeader(false);
    }
  }, [getSellerAppConfig]);

  useEffect(() => {
    getConfig();
  }, [getConfig]);

  const handleEnterQueue = async () => {
    try {
      setIsProcessing(true);
      await enterQueue();
      showNotification("success", "✓ Você entrou na fila");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao entrar na fila: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLeaveQueue = async () => {
    const result = await Swal.fire({
      title: "Sair da Fila",
      text: "Tem certeza que deseja sair da fila de atendimento?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, sair",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      setIsProcessing(true);
      await leaveQueue();
      showNotification("success", "✓ Você saiu da fila");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao sair da fila: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStartService = async () => {
    if (!queueEntry?.id) return;

    try {
      setIsProcessing(true);
      await startService(queueEntry.id);
      showNotification("success", "🎯 Atendimento iniciado!");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao iniciar: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const refreshCustomersBeforeSessionAction = async () => {
    const session = currentSession || (
      tokenSeller?.pluginspaceId && tokenSeller?.id
        ? await getCurrentSellerSession(tokenSeller.pluginspaceId, tokenSeller.id)
        : null
    );
    if (!session?.id) return [] as SellerServiceSessionCustomerItem[];

    const customers = await getSessionCustomers(session.id);
    setSessionCustomers(customers);
    if (!currentSession) refetchSession();
    return customers;
  };

  const handleReturnToQueueClick = async () => {
    const customers = await refreshCustomersBeforeSessionAction();
    if (
      sellerQueueConfig?.multiCustomerEnabled &&
      sellerQueueConfig.maxCustomersPerSession > 1 &&
      customers.length === 0
    ) {
      showNotification("error", "Não foi possível carregar os clientes do atendimento. Tente novamente.");
      return;
    }
    const pendingCustomer = customers.find((customer) => customer.status === "in_progress");
    if (pendingCustomer) {
      setSelectedCustomerId(pendingCustomer.id);
      setIsCustomerResultMode(true);
      setServiceEndIntent(null);
      setShowEndServiceModal(true);
      return;
    }
    if (customers.length > 0 && customers.every((customer) => customer.status !== "in_progress")) {
      void finalizeMultipleSession("return_to_queue");
      return;
    }
    setIsCustomerResultMode(false);
    setServiceEndIntent("return_to_queue");
    setShowEndServiceModal(true);
  };

  const handleEndAndLeaveQueueClick = async () => {
    const customers = await refreshCustomersBeforeSessionAction();
    if (
      sellerQueueConfig?.multiCustomerEnabled &&
      sellerQueueConfig.maxCustomersPerSession > 1 &&
      customers.length === 0
    ) {
      showNotification("error", "Não foi possível carregar os clientes do atendimento. Tente novamente.");
      return;
    }
    const pendingCustomer = customers.find((customer) => customer.status === "in_progress");
    if (pendingCustomer) {
      setSelectedCustomerId(pendingCustomer.id);
      setIsCustomerResultMode(true);
      setServiceEndIntent(null);
      setShowEndServiceModal(true);
      return;
    }
    if (customers.length > 0 && customers.every((customer) => customer.status !== "in_progress")) {
      void finalizeMultipleSession("end_and_leave_queue");
      return;
    }
    setIsCustomerResultMode(false);
    setServiceEndIntent("end_and_leave_queue");
    setShowEndServiceModal(true);
  };

  const handleAddCustomer = async () => {
    if (!currentSession?.id) return;
    try {
      setIsProcessing(true);
      const customer = await addSessionCustomer(currentSession.id);
      setSessionCustomers((previous) => [...previous, customer]);
      setSelectedCustomerId(customer.id);
      showNotification("success", `Cliente ${customer.sequenceNumber} adicionado ao atendimento`);
    } catch (error) {
      showNotification("error", getApiErrorMessage(error, "Não foi possível adicionar o cliente"));
    } finally {
      setIsProcessing(false);
    }
  };

  const getMultipleSessionSummary = () => ({
    outcome: sessionCustomers.some((customer) => customer.outcome === "sale_completed")
      ? "sale_completed" as const
      : "no_sale" as const,
    saleAmount: sessionCustomers.reduce(
      (total, customer) =>
        customer.outcome === "sale_completed" ? total + Number(customer.saleAmount || 0) : total,
      0,
    ),
  });

  const finalizeMultipleSession = async (intent: "return_to_queue" | "end_and_leave_queue") => {
    if (!currentSession?.id || !currentSession.sellerId) return;

    const actionLabel = intent === "end_and_leave_queue" ? "encerrar o atendimento" : "retornar à fila";
    const confirmation = await Swal.fire({
      icon: "question",
      title: intent === "end_and_leave_queue" ? "Encerrar Atendimento" : "Retornar à Fila",
      text: `Todos os clientes já possuem resultado. Deseja ${actionLabel}?`,
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmation.isConfirmed) return;

    const summary = getMultipleSessionSummary();
    try {
      setIsProcessing(true);
      if (intent === "end_and_leave_queue") {
        await closeSellerAttendance(
          currentSession.id,
          currentSession.sellerId,
          undefined,
          summary.outcome,
          undefined,
          undefined,
          summary.saleAmount,
        );
      } else {
        await returnSellerToQueueSeller(
          currentSession.id,
          undefined,
          summary.outcome,
          undefined,
          undefined,
          summary.saleAmount,
        );
      }

      await Promise.all([refetchSession(), refetchQueue()]);
      setCurrentSession(null);
      setQueueEntry(null);
      setSessionCustomers([]);
      showNotification("success", intent === "end_and_leave_queue"
        ? "✓ Atendimento encerrado"
        : "✓ Atendimento retornou para a fila");
    } catch (error) {
      showNotification("error", getApiErrorMessage(error, "Não foi possível concluir a ação"));
    } finally {
      setIsProcessing(false);
    }
  };

  const processServiceEnd = async (
    outcome: string,
    noSaleData?: NoSaleFormData,
    saleAmount?: number,
    note?: string,
  ) => {
    if (!serviceEndIntent && !isCustomerResultMode) {
      showNotification("error", "Selecione a ação de encerramento novamente");
      return;
    }

    const sellerId = currentSession?.sellerId || tokenSeller?.id;
    let sessionId = currentSession?.id;

    if (!sessionId && tokenSeller?.pluginspaceId && sellerId) {
      const latestSession = await getCurrentSellerSession(tokenSeller.pluginspaceId, sellerId);
      sessionId = latestSession?.id;
    }

    if (!sessionId || !sellerId) {
      showNotification("error", "Nenhum atendimento em progresso para encerrar");
      return;
    }

    if (outcome !== "sale_completed" && outcome !== "no_sale") {
      showNotification("error", "Resultado inválido para encerramento");
      return;
    }

    try {
      setIsProcessing(true);

      const selectedCustomer = sessionCustomers.find((item) => item.id === selectedCustomerId);
      if (isCustomerResultMode && selectedCustomer) {
        const reasonCode = outcome === "no_sale" ? noSaleData?.reasonCode ?? undefined : undefined;
        await completeSessionCustomer(selectedCustomer.id, outcome as "sale_completed" | "no_sale", saleAmount, reasonCode, note || noSaleData?.note);
        const updatedCustomers = await getSessionCustomers(sessionId);
        setSessionCustomers(updatedCustomers);
        setSelectedCustomerId(updatedCustomers.find((item) => item.status === "in_progress")?.id || null);
        setShowEndServiceModal(false);
        setServiceEndIntent(null);
        setIsCustomerResultMode(false);
        showNotification("success", `Resultado do Cliente ${selectedCustomer.sequenceNumber} salvo`);
        return;
      }

      const reasonCode = outcome === "no_sale" ? noSaleData?.reasonCode ?? undefined : undefined;
      const sessionNote = noSaleData?.note;

      if (serviceEndIntent === "end_and_leave_queue") {
        await closeSellerAttendance(sessionId, sellerId, undefined, outcome, reasonCode, sessionNote, saleAmount);
        showNotification("success", "✓ Atendimento encerrado e vendedor removido da fila");
      } else {
        await returnSellerToQueueSeller(sessionId, undefined, outcome, reasonCode, sessionNote, saleAmount);
        showNotification("success", "✓ Atendimento encerrado e vendedor retornou para a fila");
      }

      await Promise.all([refetchSession(), refetchQueue()]);
      setCurrentSession(null);
      setQueueEntry(null);

      setShowEndServiceModal(false);
      setServiceEndIntent(null);
    } catch (error) {
      showNotification(
        "error",
        `Erro ao encerrar: ${getApiErrorMessage(error, "Erro desconhecido")}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOutcomeSelected = async (outcome: string, saleAmount?: number, note?: string) => {
    await processServiceEnd(outcome, undefined, saleAmount, note);
  };

  const handleNoSaleSubmit = async (data: NoSaleFormData) => {
    await processServiceEnd("no_sale", data);
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const tokenSeller = getTokenSeller();
  if (!tokenSeller) {
    return (
      <div className="sellerAttendanceHome">
        <div className="content-attendance">
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
            <p style={{ color: "#999", fontSize: "1em" }}>Erro: Vendedor não autenticado</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sellerAttendanceHome">
      {attendanceMenuOpen && typeof document !== "undefined"
        ? createPortal(
            <>
              <div
                className="attendance-menu-overlay"
                onClick={() => setAttendanceMenuOpen(false)}
              />
              <LeftMenu
                extraSpacingLeftMenu
                cbSelected={(selected) => {
                  if (selected) {
                    setAttendanceMenuOpen(false);
                  }
                }}
              />
            </>,
            document.body
          )
        : null}

      <div className="content-attendance">
        {!loadingHeader && (
          <Header
            background={
              config?.webappcolor.backgroundheader ? config.webappcolor.backgroundheader : "#F5F5F5"
            }
            colorIcon={config?.webappcolor.iconsheader ? config.webappcolor.iconsheader : "#636363"}
            logo={config?.icon ? config.icon : ""}
            backgroundCart={config?.webappcolor.backgroundcart ? config.webappcolor.backgroundcart : "#FFBA49"}
            fontCart={config?.webappcolor.fontcart ? config.webappcolor.fontcart : "#000"}
            noSales={
              config && config.content && config.content.pdv && config.content.pdv.noSales
                ? config.content.pdv.noSales
                : false
            }
            extraSpacingLeftMenu
            hideLeftMenu
            leftMenuAction={() => setAttendanceMenuOpen(true)}
          />
        )}

        {loadingHeader ? (
          <div style={{ width: "100%", height: "calc(100vh)", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
          </div>
        ) : (
          <div className="attendance-values">
            {notification && (
              <div className={`notification notification-${notification.type}`}>
                {notification.message}
              </div>
            )}

            <div className="page-header">
              <h1>Fila de Atendimento</h1>
              {currentSession?.status === "in_progress" && <div className="timer-display">⏱ {formatTime(elapsedTime)}</div>}
            </div>

            <SellerQueueCard
              queueEntry={queueEntry}
              isLoading={isLoadingQueue}
              error={errorQueue}
              elapsedTime={elapsedTime}
              onEnterQueue={handleEnterQueue}
              onLeaveQueue={handleLeaveQueue}
              onStartService={handleStartService}
              onAddCustomer={handleAddCustomer}
              canAddCustomer={Boolean(
                sellerQueueConfig?.multiCustomerEnabled &&
                sellerQueueConfig.maxCustomersPerSession > 1 &&
                sessionCustomers.length < sellerQueueConfig.maxCustomersPerSession,
              )}
              onReturnToQueue={handleReturnToQueueClick}
              onEndService={handleEndAndLeaveQueueClick}
              isServiceActive={currentSession?.status === "in_progress"}
              isLeaveLoading={isProcessing}
              isStartServiceLoading={isProcessing}
            />

            {currentSession?.status === "in_progress" && (
              <div
                style={{
                  marginTop: -16,
                  padding: 16,
                  background: "#fff",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <strong style={{ textAlign: "center" }}>
                    Clientes deste atendimento ({sessionCustomers.length})
                  </strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    borderTop: "1px solid #eef0f3",
                    paddingTop: 12,
                  }}
                >
                  {sessionCustomers.map((customer) => (
                    <button
                      type="button"
                      key={customer.id}
                      onClick={() => {
                        if (customer.status !== "in_progress") return;
                        setSelectedCustomerId(customer.id);
                        setIsCustomerResultMode(true);
                        setServiceEndIntent(null);
                        setShowEndServiceModal(true);
                      }}
                      disabled={customer.status !== "in_progress"}
                      title={customer.status === "in_progress" ? "Informar resultado do cliente" : "Cliente já concluído"}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 6,
                        border: selectedCustomerId === customer.id ? "2px solid #0d6efd" : "1px solid #ddd",
                        background: customer.status === "in_progress" ? "#fff8e1" : "#f1f8f4",
                        cursor: customer.status === "in_progress" ? "pointer" : "default",
                        opacity: customer.status === "in_progress" ? 1 : 0.8,
                      }}
                    >
                      Cliente {customer.sequenceNumber} - {customer.status === "in_progress" ? "pendente" : "concluído"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <AttendanceModal
              isOpen={showEndServiceModal}
              isLoading={isProcessing}
              onClose={() => {
                setShowEndServiceModal(false);
                setServiceEndIntent(null);
                setIsCustomerResultMode(false);
              }}
              customerLabel={
                isCustomerResultMode
                  ? `Cliente ${sessionCustomers.find((item) => item.id === selectedCustomerId)?.sequenceNumber || ""}`
                  : undefined
              }
              onOutcomeSelected={handleOutcomeSelected}
              onNoSaleSubmit={handleNoSaleSubmit}
            />
          </div>
        )}

        {!loadingHeader && (
          <Menu
            activeMenu="attendance"
            fontColor={config?.webappcolor.iconsfooter ? config.webappcolor.iconsfooter : "#636363"}
            activeFontColor={config?.webappcolor.highlightfooter ? config.webappcolor.highlightfooter : "#000"}
            backgroundMenu={config?.webappcolor.backgroundfooter ? config.webappcolor.backgroundfooter : "#F5F5F5"}
            colorIcons={config?.webappcolor.iconsfooter ? config.webappcolor.iconsfooter : "#636363"}
            highlightColorIcons={config?.webappcolor.highlightfooter ? config.webappcolor.highlightfooter : "#636363"}
            backgroundCart={config?.webappcolor.backgroundcart ? config.webappcolor.backgroundcart : "#FFBA49"}
            fontCart={config?.webappcolor.fontcart ? config.webappcolor.fontcart : "#000"}
            noSales={
              config && config.content && config.content.pdv && config.content.pdv.noSales
                ? config.content.pdv.noSales
                : false
            }
          />
        )}
      </div>
    </div>
  );
};

export { SellerAttendance };
